import React, { useEffect, useState } from 'react';
import { Rule } from './types';
import { PriceRuleRow } from './PriceRuleRow';
import { v4 as uuidv4 } from 'uuid';
import { Button, Stack, Box, Dialog, DialogTitle, DialogContent, TextField } from '@mui/material';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useNotify } from "react-admin";

interface Props {
    value: string;
    onEditorChange: (rules: string) => void;
}

export const PriceCalculator: React.FC<Props> = ({ value, onEditorChange }) => {
    const [rules, setRules] = useState<Rule[]>([]);
    const notify = useNotify();

    const [importOpen, setImportOpen] = useState(false);
    const [importJsonData, setImportJsonData] = useState(null);
  
    const handleImportOpen = () => {
      setImportJsonData(null);
      setImportOpen(true);
    };
  
    const handleImportClose = () => {
      setImportOpen(false);
      setImportJsonData(null);
    };

    useEffect(() => {
        if (isValidJSON(value)) {
            let parsedData = JSON.parse(value);

            if (isValidData(parsedData)) {
                setRules(parsedData);
            }
        }
    }, [value]);

    const addRule = () => {
        const updatedRules = [
            ...rules,
            {
                id: uuidv4(),
                conditions: [
                    {
                        attribute: '',
                        operator: '=',
                        value: '',
                        chainOperator: undefined,
                    }
                ],
                action: {
                    operation: undefined,
                    values: []
                }
            }
        ];
    
        setRules(updatedRules as Rule[]);
        onEditorChange(JSON.stringify(updatedRules, null, 2));
    };

    const updateRule = (id: string, updated: Rule) => {
        const updatedRules = rules.map(r => (r.id === id ? updated : r));
        
        setRules(updatedRules);
        onEditorChange(JSON.stringify(updatedRules, null, 2));
    };

    const updateRules = (rules: Rule[]) => {
        setRules(rules);
        onEditorChange(JSON.stringify(rules, null, 2));
      };

    const removeRule = (id: string) => {
        const updatedRules = rules.filter(r => r.id !== id);

        setRules(updatedRules);
        onEditorChange(JSON.stringify(updatedRules, null, 2));
    };

    const handleDragEnd = (event: any) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = rules.findIndex(r => r.id === active.id);
            const newIndex = rules.findIndex(r => r.id === over.id);

            const updatedRules = arrayMove(rules, oldIndex, newIndex);

            setRules(updatedRules);
            onEditorChange(JSON.stringify(updatedRules, null, 2));
        }
    };

    const copyJson = () => {
        navigator.clipboard.writeText(JSON.stringify(rules, null, 2));
        notify(`JSON has been copied to the clipboard.`, { type: 'success' });
    };
    
    const importJson = () => {
        if (isValidJSON(importJsonData)) {
          let parsedData = JSON.parse(importJsonData);
    
          if (isValidData(parsedData)) {
            updateRules(parsedData);
            handleImportClose();
            notify(`JSON has been imported successfully.`, { type: 'success' });
          } else {
            notify(`JSON data is not compatible with price calculator`, { type: 'error' });
          }
        } else {
          notify(`Wrong format, must be JSON format.`, { type: 'error' });
        }
    };
    
    return (
        <>
            <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={rules.map(r => r.id)} strategy={verticalListSortingStrategy}>
                    {rules.map((rule, idx) => (
                        <PriceRuleRow
                            key={rule.id}
                            id={rule.id}
                            rule={rule}
                            onChange={r => updateRule(rule.id, r)}
                            onRemove={() => removeRule(rule.id)}
                        />
                    ))}
                </SortableContext>
            </DndContext>

            <Stack direction="row" spacing={2} className="price-controls">
                <Box display="flex" justifyContent="flex-start" gap={2}>
                <span onClick={addRule} className="price-button add-row">
                    + Dodaj pravilo
                </span>
                </Box>
                <Box display="flex" justifyContent="flex-end" gap={2}>
                <span onClick={copyJson} className="price-button copy-json">
                    Copy JSON
                </span>
                <span onClick={handleImportOpen} className="price-button import-json">
                    Import JSON
                </span>
                </Box>
            </Stack>

            <Dialog open={importOpen} onClose={handleImportClose} fullWidth maxWidth="sm">
                <DialogTitle>Import JSON</DialogTitle>
                <DialogContent>
                <TextField
                    label="JSON"
                    size="small"
                    multiline
                    rows={8}
                    onChange={(e) =>
                    setImportJsonData(e.target.value)
                    }
                ></TextField>
                <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
                    <Button variant="contained" color="primary" onClick={() => importJson()}>Import</Button>
                    <Button onClick={handleImportClose}>Cancel</Button>
                </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export const isValidData = (rules: Rule[]): boolean => {
    return rules.every((rule) => {
        return (
            rule.conditions.length > 0 &&
            rule.conditions.every((cond) => cond.attribute && cond.attribute !== '' && cond.operator && cond.value !== undefined && cond.value !== '') &&
            rule.action.values.length > 0 &&
            rule.action.values.every((exp) => exp.name || exp.value) &&
            rule.action.operation
        );
    });
};

export const isValidJSON = (data: string): boolean => {
    try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed);
    } catch {
        return false;
    }
};
